/**
 * SmartDetax ルートA+ / 仕様書 Ver.2.2
 * QRコードに埋めるUTF-8 CSV文字列を生成する（画像・印刷処理は別）。
 *
 * buildSmartDetaxPayloads({
 *   id: 12345, storeId: 1, registerId: 2, date: '2026-10-14T04:00:00Z',
 *   isTaxFree: true,
 *   items: [{
 *     name: 'フィギュア', quantity: 2, subtotal: 20000, taxRate: 10,
 *     goodsType: 1, reducedTax: 0,
 *     // optional: specialPackaging: 0, grossTotal, categoryCode,
 *     //           featureInfo, serialNumber, isTaxFreeEligible
 *   }]
 * }, { maxBytes: 650, maxItemsPerQr: 8 });
 *
 * 前提: subtotal は「値引き反映済み・消費税抜」の明細合計金額。
 * 個別の免税対象外商品・手直し代などは isTaxFreeEligible:false にする。
 * QRに入れる消費税額は、免税レジが徴収した税額0円ではなく、
 * 免税対象販売の税率別消費税相当額。
 */

const UTF8 = new TextEncoder();
const ABSOLUTE_MAX_BYTES = 2953; // QRモデル2 / Version40-L の理論値
const MAX_QR_COUNT = 10;        // 仕様書の識別子はQ1〜Q10
// 従来方式のSmartDetaxでも新仕様の特徴情報を受け付ける場合に使用。
// 実機で受け付けない場合はfalseに戻す（リファンド方式には影響なし）。
export const ENABLE_SMARTDETAX_LEGACY_FEATURES = true;
const DEFAULT_MAX_ITEMS_PER_QR = 8; // 読み取りやすさを優先：最大8明細/QR
const TAX_RATE_CODES = new Map([[1, 10], [2, 8], [3, 0], [8, 8], [10, 10]]);

const utf8Size = (s) => UTF8.encode(String(s)).length;

function assertInteger(value, name, min, max) {
  const number = Number(value);
  if (!Number.isSafeInteger(number) || number < min || number > max) {
    throw new Error(`${name}は${min}〜${max}の整数で指定してください（値: ${value}）`);
  }
  return number;
}

function cutUtf8(value, maxBytes) {
  let result = '';
  for (const char of String(value ?? '')) {
    if (utf8Size(result + char) > maxBytes) break;
    result += char;
  }
  return result;
}

// 本仕様のレコード区切り「||」や改行が商品文字に混入するのを防ぐ。
function cleanText(value) {
  return String(value ?? '')
    .replace(/\r\n|\r|\n/g, ' ')
    .replace(/\|\|/g, '｜｜')
    .replace(/[\x00-\x1f\x7f]/g, ' ');
}

function csvQuote(value) {
  return '"' + cleanText(value).replace(/"/g, '""') + '"';
}

function tokyoDateTime(dateLike) {
  if (!dateLike) throw new Error('購入日時（売上保存済みレシートのdate）が必要です');
  const date = dateLike instanceof Date ? dateLike : new Date(dateLike);
  if (Number.isNaN(date.getTime())) throw new Error('購入日時が不正です');
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
    }).formatToParts(date).map(({ type, value }) => [type, value])
  );
  return {
    date: `${parts.year}${parts.month}${parts.day}`,
    time: `${parts.hour}${parts.minute}${parts.second}`,
  };
}

function normalizeItem(item, index, purchaseDate, options) {
  const label = `商品${index + 1}`;
  const name = cutUtf8(cleanText(item.name ?? item.product_name), 40);
  if (!name) throw new Error(`${label}: 商品名がありません`);

  const quantity = assertInteger(item.quantity, `${label}の数量`, 0, 9999);
  const net = assertInteger(
    item.subtotal ?? item.taxExclusiveSubtotal,
    `${label}の税抜明細金額（subtotal）`, -999999999, 999999999
  );

  // QRの商品名は仕様書通りUTF-8で最大40byte。紙のレシートの表示は従来通り。
  // リファンド方式、および有効化した従来方式で「商品特徴情報」（10列目）を生成する。
  // 通常商品: 金額にかかわらずコードがあれば「CODE:コード」を付加する。
  // SET商品: 金額にかかわらず「SET:代表コード,構成コード...」を付加する。
  // JANは固有シリアル番号ではないので11列目には書かない。
  const normalizeJan = value => {
    const raw = String(value ?? '').replace(/[\sー－-]/g, '');
    return /^\d{8,18}$/.test(raw) ? raw : '';
  };
  const jan = normalizeJan(item.jan) || normalizeJan(item.barcode);
  const isSet = Number(item.set_sale || 0) > 0 ||
    (Array.isArray(item.setJans) && item.setJans.length > 0) ||
    Boolean(String(item.set_items ?? item.set_ids ?? '').trim());
  const originalFeature = cleanText(item.featureInfo ?? '').trim();
  let featureInfo = originalFeature;

  if (options.includeNewFields === true) {
    if (isSet) {
      const rawSetIds = item.set_ids ?? item.set_items ?? '';
      if (String(rawSetIds).trim() && !Array.isArray(item.setJans)) {
        throw new Error(`${label}: SET構成商品のJANをまだ取得できていません`);
      }
      const componentJans = Array.isArray(item.setJans) ? item.setJans : [];
      const invalidComponentJan = componentJans.find(value => !normalizeJan(value));
      if (invalidComponentJan !== undefined) {
        throw new Error(`${label}: SET構成商品のJANが不正です（${invalidComponentJan}）`);
      }
      const codes = [jan, ...componentJans.map(normalizeJan)].filter(Boolean);
      const setFeature = `SET:${codes.join(',')}`;
      featureInfo = [originalFeature, setFeature].filter(Boolean).join(' ');
    } else if (jan) {
      featureInfo = [originalFeature, `CODE:${jan}`].filter(Boolean).join(' ');
    }
  }

  const special = item.specialPackaging === true || Number(item.specialPackaging ?? 0) === 1;
  const rawTaxRate = special ? 10 : Number(item.taxRate ?? item.tax_rate ?? options.defaultTaxRate);
  if (!TAX_RATE_CODES.has(rawTaxRate)) {
    throw new Error(`${label}: 消費税率コードが不正または未指定です（値: ${rawTaxRate}）`);
  }
  const rate = TAX_RATE_CODES.get(rawTaxRate);

  let goodsType = '';
  if (purchaseDate <= '20261031') {
    const selected = special ? 0 : (item.goodsType ?? options.defaultGoodsType);
    goodsType = assertInteger(selected, `${label}の物品区分`, 0, 1);
  } else if (special) {
    goodsType = 0; // 2026/11/1以降、取込側で無視される
  }

  const reduced = special ? 0 : assertInteger(
    item.reducedTax ?? (rate === 8 ? 1 : 0), `${label}の軽減税対象区分`, 0, 1
  );

  const fields = [csvQuote(name), quantity, net, special ? 10 : rawTaxRate,
    goodsType, special ? 1 : '', reduced];

  const gross = item.grossTotal;
  const categoryCode = item.categoryCode;
  const feature = featureInfo;
  const serial = item.serialNumber;
  // 現行仕様は商品7項目まで。新仕様の任意項目は必要な場合だけ追加する。
  // 現行制度でも新仕様の受信に対応したSmartDetaxでは特徴情報を出力可能。
  const hasExtra = options.includeNewFields === true && [gross, categoryCode, feature, serial].some(
    (v) => v !== undefined && v !== null && v !== ''
  );
  if (hasExtra) {
    fields.push(gross === undefined || gross === null || gross === ''
      ? '' : assertInteger(gross, `${label}の税込販売価額`, -999999999, 999999999));

    if (categoryCode !== undefined && categoryCode !== null && categoryCode !== '') {
      if (!/^\d{2}$/.test(String(categoryCode)) || Number(categoryCode) < 1 || Number(categoryCode) > 16) {
        throw new Error(`${label}の商品分類コードは仕様書の01〜16で指定してください`);
      }
      fields.push(csvQuote(categoryCode));
    } else fields.push('');

    if (feature !== undefined && feature !== null && feature !== '') {
      const featureText = cleanText(feature);
      if (Array.from(featureText).length > 80 || utf8Size(featureText) > 240) {
        throw new Error(`${label}の商品特徴情報が80文字・240byteを超えました。SET構成JANを省略せず入力できるか確認してください`);
      }
      fields.push(csvQuote(featureText));
    } else fields.push('');

    if (serial !== undefined && serial !== null && serial !== '') {
      const serialText = String(serial).slice(0, 20); // 仕様書: 上位20桁
      if (!/^[A-Za-z0-9]+$/.test(serialText)) {
        throw new Error(`${label}のシリアルナンバーは半角英数字のみ使用できます`);
      }
      fields.push(csvQuote(serialText));
    } else fields.push('');

    // 後ろの任意フィールドは必要な位置までに短縮
    while (fields.length > 7 && fields[fields.length - 1] === '') fields.pop();
  }

  // QRコードが10個を超える場合に限り、同一商品を数量で統合できるようにする。
  // 個別シリアルや税込販売価額がある商品は安全のため統合しない。
  const canMerge = quantity > 0 && !item.serialNumber &&
    (item.grossTotal === undefined || item.grossTotal === null || item.grossTotal === '');
  const mergeKey = canMerge ? JSON.stringify([
    String(item.name ?? item.product_name ?? ''),
    jan,
    String(item.inventoryId ?? item.inventory_fk_id ?? ''),
    Number(item.price ?? item.unitPrice ?? 0),
    Number(item.discount ?? 0) / quantity,
    String(item.set_sale ?? ''),
    // 数量・合計税抜金額・税込金額欄だけ除いて各フィールドが完全一致する必要がある。
    fields.map((field, i) => ([1, 2, 7].includes(i) ? null : field))
  ]) : null;

  return { text: fields.join(','), fields, rate, net, name, quantity, mergeKey };
}

/**
 * @param {Object} receipt 保存後のレシートのスナップショット
 * @param {Object} options defaultTaxRate/defaultGoodsType/maxBytes/maxItemsPerQr/taxAmounts
 * @returns {string[]} QRそれぞれの内容（Q1から）
 */
export function buildSmartDetaxPayloads(receipt, options = {}) {
  if (!receipt || receipt.isTaxFree !== true) return [];

  const { date, time } = tokyoDateTime(receipt.date);
  const receiptNumber = String(receipt.id ?? '').trim();
  const registerNumber = String(receipt.registerId ?? '');
  const storeNumber = String(receipt.storeId ?? '');

  if (!/^\d{1,10}$/.test(receiptNumber)) {
    throw new Error(`レシート番号は1〜10桁の数字で指定してください（受信値: ${JSON.stringify(receipt.id)}）`);
  }
  if (!registerNumber) throw new Error('レジ番号（registerId）がありません');

  const maxBytes = assertInteger(options.maxBytes ?? 650, 'QRごとのUTF-8最大バイト数', 1, ABSOLUTE_MAX_BYTES);
  const maxItemsPerQr = assertInteger(options.maxItemsPerQr ?? DEFAULT_MAX_ITEMS_PER_QR, 'QRごとの最大明細数', 1, 80);
  // 新仕様の項目はリファンド方式に加え、スイッチ有効時の従来方式にも付加。
  // 取込互換性はSmartDetaxの実機/テスト環境で確認すること。
  const includeNewFields = receipt.taxFreeMode === 'refund' || ENABLE_SMARTDETAX_LEGACY_FEATURES;
  const items = (receipt.items ?? [])
    .filter((item) => item.isTaxFreeEligible !== false)
    .map((item, index) => normalizeItem(item, index, date, {
      ...options,
      includeNewFields,
    }));
  if (items.length === 0) throw new Error('QR化する免税対象商品の明細がありません');

  const taxBaseByRate = new Map();
  items.forEach(({ rate, net }) => taxBaseByRate.set(rate, (taxBaseByRate.get(rate) ?? 0) + net));
  const taxAmounts = options.taxAmounts || {};
  const taxSummary = [...taxBaseByRate.keys()]
    .sort((a, b) => a - b)
    .filter((rate) => rate !== 0)
    .map((rate) => {
      const override = taxAmounts[rate];
      const amount = override === undefined
        ? Math.floor(taxBaseByRate.get(rate) * rate / 100)
        : assertInteger(override, `${rate}%の税額`, 0, 999999999);
      return `${rate}:${amount}`;
    }).join('.');
  if (!taxSummary) throw new Error('税率別消費税額を作成できません（税率0%以外の免税対象商品が必要です）');

  const header = (qrNumber, total) => [
    `Q${qrNumber}`, date, time, csvQuote(storeNumber), csvQuote(registerNumber),
    csvQuote(receiptNumber), total, csvQuote(taxSummary)
  ].join(',');

  // 最大ケース(Q10/合計10)で事前分割し、最終的に実際の合計数で埋め直す。
  const headerBudget = utf8Size(header(MAX_QR_COUNT, MAX_QR_COUNT));

  // 最大8明細、かつQR 1個650byte以下。長い商品名が多ければ8明細未満でも分割。
  function splitItems(sourceItems) {
    const result = [];
    let current = [];
    let size = headerBudget;

    for (const item of sourceItems) {
      const itemBytes = utf8Size(item.text) + 2; // 先頭区切り「||」分
      if (headerBudget + itemBytes > maxBytes) {
        throw new Error(`${item.name}: 明細1件がQRの最大容量を超えています（${maxBytes}byte）`);
      }
      if (current.length > 0 &&
          (current.length >= maxItemsPerQr || size + itemBytes > maxBytes)) {
        result.push(current);
        current = [];
        size = headerBudget;
      }
      current.push(item);
      size += itemBytes;
    }
    if (current.length > 0) result.push(current);
    return result;
  }

  // 10個を超えたときだけ同一商品行を合算する。会計データ自体は変更せず、
  // QR用明細の「数量」「金額」だけ合計。税額の計算元は元の明細合計のまま。
  function mergeIdenticalItems(sourceItems) {
    const output = [];
    const byKey = new Map();
    for (const item of sourceItems) {
      const previous = item.mergeKey ? byKey.get(item.mergeKey) : null;
      if (previous && previous.net * item.quantity === item.net * previous.quantity &&
          previous.quantity + item.quantity <= 9999 &&
          Math.abs(previous.net + item.net) <= 999999999) {
        previous.quantity += item.quantity;
        previous.net += item.net;
        previous.fields[1] = previous.quantity;
        previous.fields[2] = previous.net;
        previous.text = previous.fields.join(',');
      } else {
        const copied = { ...item, fields: item.fields.slice() };
        output.push(copied);
        if (item.mergeKey) byKey.set(item.mergeKey, copied);
      }
    }
    return output;
  }

  let chunks = splitItems(items);
  if (chunks.length > MAX_QR_COUNT) {
    const groupedItems = mergeIdenticalItems(items);
    if (groupedItems.length < items.length) {
      chunks = splitItems(groupedItems);
    }
  }
  if (chunks.length > MAX_QR_COUNT) {
    throw new Error(
      `QRが${chunks.length}個必要です（最大${maxItemsPerQr}明細/QR、${maxBytes}byte/QR）。` +
      `SmartDetax仕様のQ1〜Q10を超えるため、商品を省略せず出力を中止しました。`
    );
  }

  return chunks.map((chunk, index) => {
    const text = [header(index + 1, chunks.length), ...chunk.map(item => item.text)].join('||');
    if (utf8Size(text) > maxBytes) throw new Error('QR分割容量の内部チェックに失敗しました');
    return text;
  });
}
